'use client';

import dynamic from 'next/dynamic';
import { useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Placeholder } from '@lmn/design-system';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { pageSectionIds } from '@/app/components/hotel-detail/common/consts';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useDetailPageTitle } from './hooks/useDetailPageTitle';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';
import HeroGallery from './HeroGallery';
import TSHGallery from './tsh/TSHGallery';
import HeroHotelInformation, { HeroHotelInformationInfoContainer } from './HeroHotelInformation';
import HeroAdditionalInfo from './HeroAdditionalInfo';
import { useIsMobile } from './hooks/useMediaSSR';

const TshInfoBanner = dynamic(() => import('@/app/components/hotel-detail/tsh/TSHInfoBanner'), {
  ssr: false,
});

const HeroAdditionalInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  padding: 0 16px 16px;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};

  @media ${customCss.breakpoints.sm} {
    gap: 24px;
    margin: 16px 24px 0;
    padding: 24px;
    border-radius: 8px;
  }

  @media ${customCss.breakpoints.lg} {
    margin: 16px 24px 0;
  }

  @media ${customCss.breakpoints.lgPlus} {
    margin: 16px 40px 0;
  }

  @media ${customCss.breakpoints.xl} {
    margin: 16px 0 0;
  }
`;

type HeroProps = React.HTMLAttributes<HTMLElement>;

const Gallery = styled.div`
  grid-area: gallery;
  position: relative;

  @media ${customCss.breakpoints.sm} {
    padding: 0 24px;
  }

  @media ${customCss.breakpoints.lg} {
    padding: 0 24px;
  }

  @media ${customCss.breakpoints.lgPlus} {
    padding: 0 40px;
  }

  @media ${customCss.breakpoints.xl} {
    padding: 0;
  }
`;

const HeroStyled = styled.div`
  width: 100%;
  display: grid;
  align-items: center;
  grid-template-columns: auto;
  grid-template-rows: repeat(5, auto);
  grid-template-areas:
    'gallery'
    'promotion'
    'hotel-info'
    'hotel-additional-info'
    'tsh-info';

  @media ${customCss.breakpoints.sm} {
    background-color: unset;
    grid-template-areas:
      'hotel-info'
      'gallery'
      'promotion'
      'tsh-info'
      'hotel-additional-info';
  }

  @media ${customCss.breakpoints.xl} {
    grid-template-columns: minmax(auto, 1200px);
  }
`;

export default function Hero(props: HeroProps) {
  const { offersData } = useOffersContext();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { data: staticData } = useFetchHotelStatic();

  useDetailPageTitle();

  useEffect(() => {
    sendCuriosityClickEvent(
      {
        event_name: 'event_page_load',
      },
      { onlyOnce: true }
    );
  }, []);

  const uniqueImages = useMemo(
    () => (staticData?.images ? Array.from(new Set(staticData.images)) : []),
    [staticData?.images]
  );

  return staticData ? (
    <HeroStyled data-testid="Hero" {...props}>
      <Gallery>
        {staticData.isTopSecret ? (
          <TSHGallery images={uniqueImages} discount={offersData?.cheapestFilteredMealPlan?.discountPercentage} />
        ) : (
          <HeroGallery
            name={staticData.name}
            images={uniqueImages}
            css={css`
              width: 100%;
              flex: 1 0 auto;
            `}
          />
        )}
      </Gallery>
      {staticData?.isTopSecret ? (
        <TshInfoBanner
          css={css`
            grid-area: tsh-info;

            margin: 16px 0 0;

            @media ${customCss.breakpoints.sm} {
              margin: 16px 40px 0;
              border-radius: 8px;
            }

            @media ${customCss.breakpoints.lg} {
              margin: 16px 24px 0;
            }

            @media ${customCss.breakpoints.lgPlus} {
              margin: 16px 40px 0;
            }

            @media ${customCss.breakpoints.xl} {
              margin: 16px 0 0;
            }
          `}
        />
      ) : null}
      {staticData?.name ? (
        <>
          <HeroHotelInformation />
          <HeroAdditionalInfoContainer
            id={pageSectionIds.generalInformation}
            css={`
              grid-area: hotel-additional-info;
            `}
          >
            <HeroAdditionalInfo />
          </HeroAdditionalInfoContainer>
        </>
      ) : (
        <div
          css={`
            @media ${customCss.breakpoints.sm} {
              height: 16px;
            }
          `}
        />
      )}
    </HeroStyled>
  ) : (
    <HeroSkeleton {...props} />
  );
}

function HeroSkeleton(props: HeroProps) {
  const { commonData } = usePageWrapperData();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  return (
    <HeroStyled data-testid="Hero" {...props}>
      <GallerySkeleton />
      {isMobile ? <InfoSkeletonMobile /> : <InfoSkeletonDesktop />}
      {isMobile ? <HeroAdditionalInfoSkeletonMobile /> : <HeroAdditionalInfoSkeletonDesktop />}
    </HeroStyled>
  );
}

function GallerySkeleton() {
  return (
    <Gallery
      css={`
        grid-area: gallery;
        div {
          border-radius: 0;
          height: 260px;
          @media ${customCss.breakpoints.sm} {
            border-radius: 8px;
            height: 300px;
          }
          @media ${customCss.breakpoints.xl} {
            height: 400px;
          }
        }
      `}
    >
      <Placeholder height="100%" width="100%" />
    </Gallery>
  );
}

const Separator = styled.div`
  min-height: 1px;
  background-color: ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
  width: 100%;
  margin: 8px 0;
`;

function InfoSkeletonMobile() {
  return (
    <HeroHotelInformationInfoContainer
      css={css`
        gap: 8px;
        background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
      `}
    >
      <Placeholder height="30px" width="278px" />
      <Placeholder height="16px" width="80px" />
      <Separator />
      <div
        css={`
          display: flex;
          gap: 8px;
          align-items: center;
        `}
      >
        <Placeholder height="24px" width="24px" />
        <Placeholder height="16px" width="189px" />
      </div>
      <Separator />
    </HeroHotelInformationInfoContainer>
  );
}

function InfoSkeletonDesktop() {
  return (
    <HeroHotelInformationInfoContainer
      css={`
        gap: 20px;
        margin-top: 12px;
        margin-bottom: 8px;
      `}
    >
      <Placeholder height="40px" width="582px" />

      <div
        css={`
          display: flex;
          gap: 8px;
        `}
      >
        <Placeholder height="16px" width="140px" />
        <Placeholder height="16px" width="70%" />
      </div>
    </HeroHotelInformationInfoContainer>
  );
}

function HeroAdditionalInfoSkeletonMobile() {
  return (
    <HeroAdditionalInfoContainer
      css={`
        grid-area: hotel-additional-info;
        gap: 8px;
      `}
    >
      <Placeholder height="16px" width="100%" />
      <Placeholder height="16px" width="100%" />
      <Placeholder height="16px" width="100%" />
      <Placeholder height="16px" width="100%" />
    </HeroAdditionalInfoContainer>
  );
}

function HeroAdditionalInfoSkeletonDesktop() {
  return (
    <HeroAdditionalInfoContainer
      css={`
        grid-area: hotel-additional-info;
        gap: 16px;
      `}
    >
      <Placeholder height="24px" width="193px" />
      <div
        css={`
          display: flex;
          flex-direction: row;
          gap: 24px;
        `}
      >
        <div
          css={`
            flex-grow: 1;
            display: flex;
            flex-direction: row;
            gap: 16px;
          `}
        >
          <div
            css={`
              width: 50%;
              display: flex;
              flex-direction: column;
              gap: 12px;
            `}
          >
            <Placeholder height="20px" width="100%" />
            <Placeholder height="20px" width="100%" />
            <Placeholder height="20px" width="100%" />
            <Placeholder height="20px" width="100%" />
          </div>
          <div
            css={`
              width: 50%;
              display: flex;
              flex-direction: column;
              gap: 12px;
            `}
          >
            <Placeholder height="20px" width="100%" />
            <Placeholder height="20px" width="100%" />
            <Placeholder height="20px" width="100%" />
            <Placeholder height="20px" width="100%" />
          </div>
        </div>
        <div
          css={`
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            gap: 12px;
          `}
        >
          <Placeholder height="20px" width="100%" />
          <Placeholder height="20px" width="100%" />
          <Placeholder height="20px" width="100%" />
          <Placeholder height="20px" width="100%" />
        </div>
      </div>
    </HeroAdditionalInfoContainer>
  );
}
